import type { OfflineLedgerEntry } from './offline-queue'
import { createOfflineLedger, isOwnedOfflineEnvelope } from './offline-queue'

export interface OfflineStorage {
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
  removeItem(key: string): Promise<void>
}

/** A user's outbox survives sign-out; it is only restored for its original owner. */
export function createOwnedOfflineLedger(
  storage: OfflineStorage,
  legacyKey: string,
) {
  const knownOwners = new Set<string>()
  let writes = Promise.resolve()
  const keyFor = (ownerId: string) =>
    `${legacyKey}:v2:${encodeURIComponent(ownerId)}`
  const ledger = createOfflineLedger(entries => {
    const groups = new Map<string, OfflineLedgerEntry[]>()
    for (const entry of entries) {
      if (!isOwnedOfflineEnvelope(entry.envelope)) continue
      const owner = entry.envelope.ownerId
      knownOwners.add(owner)
      groups.set(owner, [...(groups.get(owner) ?? []), entry])
    }
    const snapshots = [...knownOwners].map(
      owner => [keyFor(owner), groups.get(owner) ?? []] as const,
    )
    writes = writes
      .catch(() => undefined)
      .then(async () => {
        for (const [key, entries] of snapshots) {
          if (entries.length)
            await storage.setItem(key, JSON.stringify(entries))
          else await storage.removeItem(key)
        }
      })
    // Preserve rejection for flush(); background writes must not become unhandled.
    void writes.catch(error =>
      console.warn('[OfflineQueue] Persistence failed', error),
    )
  })

  return {
    ledger,
    flush: () => writes,
    async restore(ownerId: string | null): Promise<void> {
      // Old work cannot be attributed safely. Keep its original bytes for
      // recovery, but never expose or automatically replay them under a login.
      const legacy = await storage.getItem(legacyKey)
      if (legacy !== null) {
        await storage.setItem(`${legacyKey}:quarantine-v1`, legacy)
        await storage.removeItem(legacyKey)
      }
      if (!ownerId) return
      await writes.catch(() => undefined)
      const raw = await storage.getItem(keyFor(ownerId))
      if (!raw) return
      const parsed: unknown = JSON.parse(raw)
      if (!Array.isArray(parsed)) return
      const entries = parsed.filter(
        (entry): entry is OfflineLedgerEntry =>
          !!entry &&
          typeof entry === 'object' &&
          isOwnedOfflineEnvelope(entry.envelope) &&
          entry.envelope.ownerId === ownerId &&
          typeof entry.updatedAt === 'number',
      )
      knownOwners.add(ownerId)
      ledger.hydrate(entries)
      ledger.prune()
    },
  }
}
